// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useEffect, useRef, useState, useCallback } from 'preact/hooks';
import { useRoute } from 'preact-iso';
import { api, isOfferUnavailable, type GetVaultDetailsResponse, type ContractResponse, type InitiateWithdrawalResponse, type OfferResponse, type VaultTx } from '../api.js';
import type { PsbtInspection, WithdrawalAttestationInput } from '@cobault/key-agent';
import { formatBtc, formatDate, formatPercent, formatUsd, periodRewardFraction, truncate } from '../format.js';
import { useSession } from '../session-context.js';
import { DepositAddressField } from '../components/deposit-address-field.js';
import { subStatusLabel } from './home.js';
import { ProductSubtitle } from '../components/product-subtitle.js';
import { VerifierConfirmModal } from '../components/verifier-confirm-modal.js';
import { verifierOriginForNetwork } from '../verifier-embed.js';
import { sealVaultAnchorBestEffort } from '../vault-anchor.js';
import { useVerifierIdentity, type VerifierIdentity } from '../spa-config.js';
import '../styles/vault-detail.css';

// ── Lifecycle progress steps (Phase 5) ────────────────────────────────
//
// Phase 5 replaces the previous 8-row vertical timeline with a 5-step
// horizontal progress bar. The state→step mapping collapses:
//   ACCEPTED             → Open
//   FUNDING + FUNDED     → Funding
//   ACTIVE               → Active
//   EXPIRED              → Withdraw
//   SETTLING + SETTLED   → Settled
//   CLOSED               → terminal (index past the last step)
//   ABANDONED / FAILED   → no progress bar (handled by hero + banner)

export const PROGRESS_STEPS = [
  { key: 'open', label: 'Open' },
  { key: 'funding', label: 'Funding' },
  { key: 'active', label: 'Active' },
  { key: 'withdraw', label: 'Withdraw' },
  { key: 'settled', label: 'Settled' },
] as const;

/** Index of the active (or last-completed) progress step for a given
 *  state. Returns `-1` for states that don't render a progress bar
 *  (ABANDONED, FAILED, unknown). `PROGRESS_STEPS.length` (= 5) means
 *  every step is complete — only CLOSED reaches this. */
export function progressStepIndex(state: string): number {
  switch (state.toUpperCase()) {
    case 'ACCEPTED': return 0;
    case 'FUNDING':
    case 'FUNDED': return 1;
    case 'ACTIVE': return 2;
    case 'EXPIRED': return 3;
    case 'SETTLING':
    case 'SETTLED': return 4;
    case 'CLOSED': return PROGRESS_STEPS.length;
    default: return -1;
  }
}

/** Inline detail label for the currently-active progress step. The
 *  internal FUNDED yield-cycle and refund sub-statuses carry their own
 *  verbs (see `subStatusLabel`); returns the canonical step description
 *  when no sub-status override applies. */
export function progressStepDescription(
  state: string,
  sub_status: string | undefined,
): string {
  const subLabel = subStatusLabel(sub_status);
  if (subLabel) return subLabel;
  switch (state.toUpperCase()) {
    case 'ACCEPTED': return 'Awaiting deposit';
    case 'FUNDING': return 'Confirming deposit';
    case 'FUNDED': return 'Setting up vault';
    case 'ACTIVE': return 'Earning yield';
    case 'EXPIRED': return 'Ready to settle';
    case 'SETTLING': return 'Settling';
    case 'SETTLED': return 'Reward sent';
    case 'CLOSED': return 'Closed';
    default: return '';
  }
}

const TERMINAL_STATES = new Set(['CLOSED', 'ABANDONED', 'FAILED']);

/** V08b: pick the Cobault Verifier SPA URL for a given vault, keyed on
 *  the current bitcoin network. Hard-coded on purpose — the verifier
 *  origin is load-bearing for the cross-origin ownership-signing flow
 *  and a typo'd env var must never widen the trust boundary. Returns
 *  `null` for unknown networks (the "Verify independently…" link is
 *  suppressed in that case rather than pointing at a wrong host). */
export function verifierVaultUrlForNetwork(
  network: string | null,
  vaultId: string,
): string | null {
  switch (network) {
    case 'regtest':
      return `http://localhost:5174/v/${vaultId}`;
    case 'signet':
      return `https://verifier.staging.cobault.io/v/${vaultId}`;
    case 'testnet':
    case 'bitcoin':
      return `https://app.verifier.cobault.io/v/${vaultId}`;
    default:
      return null;
  }
}

// ── Hero state line (Phase 5) ─────────────────────────────────────────

interface VaultHeroCopy {
  primary: string;
  secondary: string;
}

interface VaultHeroInput {
  state: string;
  principal_sats: number;
  reward_sats: number;
  user_sats?: number;
  updated_at_unix: number;
  /** COB-152: current wall-clock (unix seconds), passed in so the FUNDED
   *  dwell calculation stays a pure, testable function of its inputs rather
   *  than reading the clock itself. */
  now_unix: number;
  error_message?: string;
  /** B2: distinguishes a user-returned deposit (`refund_confirmed`) from a
   *  timed-out one in the ABANDONED hero copy. */
  sub_status?: string;
  /** COB-701: whether the deposit address is currently revealed. COB-245
   *  withholds it until the owner confirms the contract on the verifier, and
   *  the ACCEPTED secondary line must not say "below" while nothing is. */
  deposit_address_revealed?: boolean;
  /** Live-ticking countdown string, e.g. "5d 12h 04m 12s". Empty when
   *  the settlement time has passed or the state does not show a
   *  countdown. */
  countdownStr: string;
  /** Part B: the in-flight on-chain confirmation wait, when one is
   *  active (deposit during FUNDING; reward during FUNDED ·
   *  reward_broadcast). Drives the "N of M confirmations" secondary
   *  line. `undefined` when there is nothing to count (see
   *  `inflightConfirmations`), and the state's generic copy is used. */
  pendingConfirmations?: { confirmations: number; required: number };
}

/** "N of M confirmations." — the shared confirmation-counter line. */
function confirmationsLine(p: { confirmations: number; required: number }): string {
  return `${p.confirmations} of ${p.required} confirmations.`;
}

/** COB-152: how long a FUNDED vault may sit WITHOUT a sub_status transition
 *  (`updated_at_unix` is bumped on every transition) before the hero switches
 *  from the indefinite "Generating your yield." to reassuring "taking longer —
 *  deposit safe, auto-refunded if we can't proceed" copy. Set above the
 *  watcher's normal trade-setup window so a merely-slow activation doesn't
 *  trip it; a genuinely stuck vault keeps showing it until the watcher
 *  resolves it (auto-refund or, rarely, a terminal failure with its own UI). */
const FUNDED_SLOW_AFTER_SECS = 180;

/** Pure helper for the per-state hero copy. Exposed for unit tests;
 *  the `VaultHero` component wires up the live `useCountdown` hook and
 *  passes the string in via `countdownStr`. */
export function vaultDetailHero(input: VaultHeroInput): VaultHeroCopy {
  const principalBtc = `${formatBtc(input.principal_sats)} BTC`;
  const rewardBtc = `${formatBtc(input.reward_sats)} BTC`;
  const state = input.state.toUpperCase();
  switch (state) {
    case 'ACCEPTED':
      return {
        primary: `Awaiting your ${principalBtc} deposit.`,
        // COB-701: while the address is withheld (COB-245) point at the
        // confirmation step instead of at an address that is not on the page.
        secondary:
          input.deposit_address_revealed === false
            ? 'Confirm your contract with the independent verifier to reveal the deposit address.'
            : 'Send to the address below.',
      };
    case 'FUNDING':
      return {
        primary: `Confirming your ${principalBtc} deposit on-chain.`,
        secondary: input.pendingConfirmations
          ? confirmationsLine(input.pendingConfirmations)
          : 'Awaiting block confirmations.',
      };
    case 'FUNDED':
      // COB-429 / COB-428: the owner has not confirmed the currently-registered
      // contract with the independent verifier — after a reaccept replaced it
      // (`reapproved_awaiting_ack`), or after the reward landed on a contract that
      // was never acknowledged (`awaiting_ack`).
      // Nothing proceeds until they do, so this branch runs FIRST, ahead of the
      // reward and the COB-152 slow-dwell branches: an ack-pending vault sits
      // unprogressed, so the slow branch would otherwise swallow it with "taking
      // a little longer than usual". Never names the mechanism.
      if (input.sub_status === 'reapproved_awaiting_ack') {
        return {
          primary: 'Confirm your revised contract.',
          secondary:
            'The vault resumes once you confirm the revised terms with the independent verifier.',
        };
      }
      if (input.sub_status === 'awaiting_ack') {
        return {
          primary: 'Confirm your contract to activate the vault.',
          secondary: 'The independent verifier is waiting for your confirmation.',
        };
      }
      // COB-349 follow-up: an `offer_invalid` vault is FUNDED but its agreed
      // terms were invalidated — the user must approve a revised offer before
      // the cycle resumes. This branch runs FIRST, ahead of the reward and the
      // COB-152 slow-dwell branches: a reaccept-pending vault sits unprogressed,
      // so the slow branch would otherwise swallow it with "taking a little
      // longer than usual". The page auto-opens the offer-review modal for this
      // sub_status. Never names the mechanism.
      if (input.sub_status === 'offer_invalid') {
        return {
          primary: 'Review your revised offer.',
          secondary: `Your ${principalBtc} deposit is confirmed and safe — approve the updated terms to continue.`,
        };
      }
      // FUNDED runs the yield cycle (the term is set up but not yet
      // ACTIVE). Once the reward tx is broadcasting we show "Sending
      // your reward" with the confirmation counter; before that the
      // yield is still being generated. Never names the mechanism.
      if (input.sub_status === 'reward_broadcast') {
        return {
          primary: 'Sending your reward.',
          secondary: input.pendingConfirmations
            ? confirmationsLine(input.pendingConfirmations)
            : 'Confirming on-chain.',
        };
      }
      // COB-152: a FUNDED vault that has not progressed for a while may be
      // stuck in trade-setup. Rather than show "Generating your yield."
      // indefinitely, reassure the depositor that their funds are safe and
      // will be refunded automatically if setup can't complete.
      if (input.now_unix - input.updated_at_unix > FUNDED_SLOW_AFTER_SECS) {
        return {
          primary: 'This is taking a little longer than usual.',
          secondary: `Your ${principalBtc} deposit is safe — if we can't set up your yield, it's refunded to you automatically.`,
        };
      }
      return {
        primary: 'Generating your yield.',
        secondary: `Your ${principalBtc} deposit is confirmed.`,
      };
    case 'ACTIVE':
      return {
        primary: `Your ${principalBtc} is earning yield.`,
        secondary: input.countdownStr
          ? `Settles in ${input.countdownStr}.`
          : 'Settling.',
      };
    case 'SETTLING':
      return {
        primary: `Your ${principalBtc} is settling.`,
        secondary: 'Reward incoming.',
      };
    case 'EXPIRED':
      return {
        primary: `Your ${principalBtc} deposit + ${rewardBtc} reward is ready to settle.`,
        secondary: 'Sign to receive principal + reward.',
      };
    case 'SETTLED':
      return {
        primary: 'Reward sent.',
        secondary: 'Awaiting on-chain confirmation.',
      };
    case 'CLOSED':
      return {
        primary: 'Closed.',
        secondary: input.user_sats != null
          ? `${formatBtc(input.user_sats)} BTC withdrawn ${formatDate(input.updated_at_unix)}.`
          : `Withdrawn ${formatDate(input.updated_at_unix)}.`,
      };
    case 'ABANDONED':
      return input.sub_status === 'refund_confirmed'
        ? {
            primary: 'Deposit returned.',
            secondary: 'Your deposit was returned to your payout address.',
          }
        : {
            primary: 'Abandoned.',
            secondary: 'Vault was not funded within the required timeframe.',
          };
    case 'FAILED':
      return {
        primary: 'Failed.',
        secondary: input.error_message || 'Vault did not complete successfully.',
      };
    default:
      return { primary: state, secondary: '' };
  }
}

// ── Confirmation-counter helper (Part B) ─────────────────────────────

/** The in-flight on-chain confirmation wait, if any, for the hero's
 *  "N of M confirmations" counter. Returns `null` unless the vault is
 *  actively waiting on a tx to reach the watcher's gate:
 *    - FUNDING → the deposit (deepest deposit row; a top-up may leave
 *      several, and the gate tracks the deepest).
 *    - FUNDED · reward_broadcast → the reward tx.
 *  Suppressed when the gate is trivially small (`required <= 2`:
 *  regtest/signet confirm in a block or two, so a counter is noise) or
 *  when the relevant tx isn't in the list yet. The depth is clamped to
 *  `required` so the brief window where the deposit/reward has reached
 *  the gate but the watcher hasn't yet advanced the state shows a clean
 *  "M of M" rather than overshooting. Pure; exported for tests. */
export function inflightConfirmations(
  state: string,
  subStatus: string | undefined,
  txs: VaultTx[],
  requiredConfirmations: number,
): { confirmations: number; required: number } | null {
  if (!Number.isFinite(requiredConfirmations) || requiredConfirmations <= 2) {
    return null;
  }
  const s = state.toUpperCase();
  let confirmations: number | null = null;
  if (s === 'FUNDING') {
    const depths = txs.filter((t) => t.type === 'deposit').map((t) => t.confirmations);
    if (depths.length > 0) confirmations = Math.max(...depths);
  } else if (s === 'FUNDED' && subStatus === 'reward_broadcast') {
    const reward = txs.find((t) => t.type === 'reward');
    if (reward) confirmations = reward.confirmations;
  }
  if (confirmations === null) return null;
  return {
    confirmations: Math.min(confirmations, requiredConfirmations),
    required: requiredConfirmations,
  };
}

// ── Deposit progress helpers (B1: under-deposit "remaining") ──────────

/** Sum of *confirmed* deposit credits in a vault's transaction list, in
 *  sats. The watcher sums all confirmed UTXOs at the deposit address
 *  toward the contracted principal, so this mirrors what it counts.
 *  Pure; exported for tests. */
export function confirmedDepositedSats(txs: VaultTx[]): number {
  return txs
    .filter((t) => t.type === 'deposit' && t.confirmed && t.amount_sats != null)
    .reduce((sum, t) => sum + (t.amount_sats ?? 0), 0);
}

/** Sats still owed against the contracted principal — principal minus the
 *  confirmed deposited total, floored at zero. A partial deposit can be
 *  topped up to complete; this is what's left to send. Pure; exported for
 *  tests. */
export function remainingDepositSats(principalSats: number, txs: VaultTx[]): number {
  return Math.max(0, principalSats - confirmedDepositedSats(txs));
}

/** Sum of *unconfirmed* deposit credits (seen on-chain / in the mempool but
 *  not yet confirmed), in sats. Counterpart to `confirmedDepositedSats` — an
 *  inbound deposit the watcher has detected but not yet counted toward the
 *  funding gate. Pure; exported for tests. */
export function pendingDepositedSats(txs: VaultTx[]): number {
  return txs
    .filter((t) => t.type === 'deposit' && !t.confirmed && t.amount_sats != null)
    .reduce((sum, t) => sum + (t.amount_sats ?? 0), 0);
}

/** COB-168: how a FUNDING vault's deposit progress should be presented. The
 *  watcher moves Accepted → Funding on the *first* UTXO it sees, confirmed or
 *  not (`watcher_loop.rs`), so a freshly-detected full deposit lands in FUNDING
 *  with zero *confirmed* sats — it is "detected, confirming", NOT "incomplete".
 *  We split three cases off confirmed (C), unconfirmed/in-flight (U) and the
 *  contracted principal (P):
 *    - 'complete'   — C ≥ P: nothing for the card to add; the hero's
 *                     confirmation counter carries the page (card renders null).
 *    - 'detected'   — C < P but C + U ≥ P: the shortfall is already in-flight;
 *                     reassure + confirm, never tell the user to send more.
 *    - 'incomplete' — C + U < P: genuinely short even counting in-flight; show
 *                     the address and how much is still to send.
 *  `stillToSend` discounts in-flight sats so we never over-ask. Deposit-
 *  protection-safe default: a deposit the watcher hasn't sized yet
 *  (`amount_sats` null) doesn't count as in-flight, so it falls to 'incomplete'
 *  and the address stays on screen. Pure; exported for tests. */
export type FundingDepositStatus =
  | { kind: 'complete' }
  | { kind: 'detected'; confirmed: number; pending: number; principal: number }
  | {
      kind: 'incomplete';
      confirmed: number;
      pending: number;
      principal: number;
      stillToSend: number;
    };

export function fundingDepositStatus(
  principalSats: number,
  txs: VaultTx[],
): FundingDepositStatus {
  const confirmed = confirmedDepositedSats(txs);
  if (confirmed >= principalSats) return { kind: 'complete' };
  const pending = pendingDepositedSats(txs);
  if (confirmed + pending >= principalSats) {
    return { kind: 'detected', confirmed, pending, principal: principalSats };
  }
  return {
    kind: 'incomplete',
    confirmed,
    pending,
    principal: principalSats,
    stillToSend: Math.max(0, principalSats - confirmed - pending),
  };
}

/** True when a deposit tx has been seen on-chain but not yet confirmed —
 *  an inbound top-up is mid-flight. While this holds, the "return
 *  deposit" control is shown but disabled: refunding now would race the
 *  incoming UTXO (the refund spends the confirmed deposits and would
 *  miss, or conflict with, the still-confirming one). Pure; exported for
 *  tests. */
export function hasPendingInboundDeposit(txs: VaultTx[]): boolean {
  return txs.some((t) => t.type === 'deposit' && !t.confirmed);
}

// ── Countdown helpers ─────────────────────────────────────────────────

/** Format a remaining-seconds count as "5d 12h 04m 12s". Returns "" for
 *  zero/negative remaining. Pure for testability. Minutes and seconds
 *  zero-pad to two digits so the live tick doesn't reflow. */
export function formatCountdown(remainingSeconds: number): string {
  if (!Number.isFinite(remainingSeconds) || remainingSeconds <= 0) return '';
  const days = Math.floor(remainingSeconds / 86400);
  const hours = Math.floor((remainingSeconds % 86400) / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  const seconds = remainingSeconds % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  parts.push(`${hours}h`);
  parts.push(`${String(minutes).padStart(2, '0')}m`);
  parts.push(`${String(seconds).padStart(2, '0')}s`);
  return parts.join(' ');
}

export type WithdrawalStep =
  | 'idle' | 'initiating' | 'reviewing' | 'signing' | 'broadcasting' | 'done';

/** Which withdrawal view `WithdrawalActionCard` renders for a given vault +
 *  flow step. Extracted as a pure function so the routing is unit-tested
 *  directly — most importantly the deposit-protection rule that, once the
 *  user has signed (`sub_status = 'user_withdrawal_pending_cosign'`), a
 *  reload shows the *pending* state and never re-prompts for a signature.
 *
 *  Ordering matters: an open modal wins over everything; a terminal
 *  settlement summary wins over a lingering 'done' step; "needs-signature"
 *  covers EXPIRED (first run) and a SETTLING vault still awaiting the user's
 *  signature (PSBT initiated but not yet user-signed); everything else in
 *  SETTLING — plus the transient 'done' — is "pending". */
export function withdrawalCardView(
  state: string,
  withdrawalStep: WithdrawalStep,
  vault: { withdrawal_psbt_hex?: string; sub_status?: string },
): 'modal' | 'summary' | 'needs-signature' | 'pending' | 'none' {
  const s = state.toUpperCase();
  const modalOpen =
    withdrawalStep === 'reviewing' ||
    withdrawalStep === 'signing' ||
    withdrawalStep === 'broadcasting';
  if (modalOpen) return 'modal';
  if (s === 'SETTLED' || s === 'CLOSED') return 'summary';
  // SETTLING is entered at initiate-withdrawal time, *before* the user
  // signs (the stored PSBT is verifier-signed only). sub_status flips to
  // 'user_withdrawal_pending_cosign' once the user has signed, so the vault
  // still needs the user's signature only while that sub_status is absent.
  const settlingAwaitingUserSig =
    s === 'SETTLING' &&
    !!vault.withdrawal_psbt_hex &&
    vault.sub_status !== 'user_withdrawal_pending_cosign';
  if (
    (withdrawalStep === 'idle' || withdrawalStep === 'initiating') &&
    (s === 'EXPIRED' || settlingAwaitingUserSig)
  ) {
    return 'needs-signature';
  }
  if (withdrawalStep === 'done' || s === 'SETTLING') return 'pending';
  return 'none';
}

function useCountdown(targetUnix: number): string {
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  useEffect(() => {
    const interval = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(interval);
  }, []);
  return formatCountdown(targetUnix - now);
}

// ── Main component ────────────────────────────────────────────────────

export function VaultDetail() {
  const { params } = useRoute();
  const session = useSession();
  const [vault, setVault] = useState<GetVaultDetailsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // T30: Withdrawal flow state
  const [withdrawalStep, setWithdrawalStep] = useState<WithdrawalStep>('idle');
  const [withdrawalData, setWithdrawalData] = useState<InitiateWithdrawalResponse | null>(null);
  const [withdrawalError, setWithdrawalError] = useState<string | null>(null);
  // COB-491 (R5): the root-signed verifier identity tuple the key agent checks
  // the withdrawal attestation against. Runtime config, never a build-time bake
  // — the bundle stays byte-identical across environments.
  const verifierIdentity = useVerifierIdentity();

  // F5: Offer re-approval modal
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [freshOffer, setFreshOffer] = useState<OfferResponse | null>(null);
  const [offerModalLoading, setOfferModalLoading] = useState(false);
  const [offerModalError, setOfferModalError] = useState<string | null>(null);
  // Suppress the auto-show effect briefly after a user action so we don't
  // re-open the modal while the watcher is still advancing sub_status.
  const [suppressAutoShowUntil, setSuppressAutoShowUntil] = useState(0);

  // Transaction history
  const [vaultTxs, setVaultTxs] = useState<VaultTx[]>([]);

  // Pre-T32 B2: refund-in-progress modal. Fires on first entry into the
  // F9 refund pipeline (permanent trade-execute failure → auto-refund)
  // and is dismissed with a single OK. Acknowledgement persists in
  // localStorage so it doesn't re-open on auto-refresh or page reload.
  const [showRefundModal, setShowRefundModal] = useState(false);

  // COB-263: the verifier-served confirm embed, hosted in a modal. Opened
  // from the ACCEPTED deposit card + the "Independent verifier" field.
  // `verifierOrigin` is null on unknown networks — then the in-app confirm
  // is suppressed and only the direct-verifier link is offered.
  const [showVerifierConfirm, setShowVerifierConfirm] = useState(false);
  const verifierOrigin = verifierOriginForNetwork(session.bitcoinNetwork);

  const fetchVault = useCallback(() => {
    if (!params.id) return;
    // F14.4 / F8: don't fire authenticated endpoints while the
    // session is expired/locked/absent; a stale 401 would noisily
    // re-trigger the session-expired callback in a loop.
    if (!session.isAuthenticated) return;
    // S05.6: the full contract moved behind /vault/:id/details
    // (authenticated + owner-only). The vault-detail page always
    // runs in an authenticated session and is only shown to the
    // vault owner, so we go straight to the details endpoint.
    api.getVaultDetails(params.id)
      .then(setVault)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load vault'));
    api.getVaultTransactions(params.id)
      .then(setVaultTxs)
      .catch(() => {});
  }, [params.id, session.isAuthenticated]);

  // F14.4 / F8: when session flips out of authenticated, clear any
  // previously-loaded vault so the previous tenant's data can't leak
  // into the new session's view.
  useEffect(() => {
    if (!session.isAuthenticated) {
      setVault(null);
      setVaultTxs([]);
      setError(null);
    }
  }, [session.isAuthenticated]);

  // Initial fetch
  useEffect(() => { fetchVault(); }, [fetchVault]);

  // Auto-refresh every 5s for non-terminal states
  useEffect(() => {
    if (!vault) return;
    const state = vault.state.toUpperCase();
    if (TERMINAL_STATES.has(state)) return;
    const interval = setInterval(fetchVault, 5000);
    return () => clearInterval(interval);
  }, [vault?.state, vault?.sub_status, fetchVault]);

  // Auto-show offer modal when sub_status is offer_invalid
  useEffect(() => {
    if (vault?.sub_status !== 'offer_invalid') return;
    if (showOfferModal) return;
    if (Date.now() < suppressAutoShowUntil) return;
    let cancelled = false;
    api.getOffer()
      .then(o => {
        if (cancelled) return;
        // COB-386: if there is no quotable offer right now (thin market /
        // maintenance), there is nothing to re-quote — leave the modal
        // closed, matching the prior behaviour where a 422 collapsed to a
        // swallowed error. The effect re-fires if sub_status changes.
        if (isOfferUnavailable(o)) {
          setOfferModalLoading(false);
          return;
        }
        setFreshOffer(o);
        setOfferModalLoading(false);
        setShowOfferModal(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [vault?.sub_status, suppressAutoShowUntil]);

  // COB-462: seal the COB-178 clear-sign anchor as soon as this page has seen
  // all three destinations. That is the acknowledgement moment — the deposit
  // address appears in the details response only once the owner has confirmed
  // the contract on the independent verifier (COB-245) — so this fires on the
  // `onConfirmed → fetchVault()` path, and deliberately also on a plain visit
  // to any already-acknowledged vault: every vault created between 2026-07-04
  // and this change has no sealed anchor at all, and this repairs them a whole
  // lifecycle earlier than the sign-time heal would.
  //
  // The seal itself is write-once inside the agent, so this never overwrites
  // an anchor the accept flow (or an earlier visit) already pinned — including
  // the case where the accept flow pinned the browser's OWN choice of payout
  // address rather than the server's echo of it. One attempt per page load:
  // the 5s auto-refresh must not turn a benign failure into a warn loop.
  const anchorSealAttempted = useRef(false);
  useEffect(() => {
    if (anchorSealAttempted.current) return;
    if (!session.isAuthenticated || !session.agent) return;
    if (!vault?.deposit_address) return;
    anchorSealAttempted.current = true;
    void sealVaultAnchorBestEffort(session.agent, vault.vault_id, {
      userPayoutAddress: vault.contract.user_payout_address,
      platformRefundAddress: vault.contract.platform_refund_address,
      depositAddress: vault.deposit_address,
    });
  }, [vault?.vault_id, vault?.deposit_address, session.agent, session.isAuthenticated]);

  // Auto-show refund modal when the watcher has routed the vault into
  // the F9 refund pipeline. We fire on any pre-terminal refund state so
  // a user landing mid-refund (after a page reload or new session) still
  // sees the notification. `refund_confirmed` is excluded — by then the
  // refund has already landed and the modal would be announcing stale
  // news; the vault will soon flip to terminal FAILED which has its own
  // banner. Acknowledgement is persisted per vault_id so refreshing
  // the page doesn't re-open a dismissed modal.
  useEffect(() => {
    if (!vault) return;
    const sub = vault.sub_status;
    const inRefundPipeline =
      sub === 'refund_initiating' ||
      sub === 'refund_co_signing' ||
      sub === 'refund_broadcast';
    if (!inRefundPipeline) return;
    const ackKey = `vault-refund-acked-${vault.vault_id}`;
    if (localStorage.getItem(ackKey) === '1') return;
    setShowRefundModal(true);
  }, [vault?.vault_id, vault?.sub_status]);

  if (!session.isAuthenticated) {
    return (
      <div class="vault-detail">
        <div class="home-auth home-auth--wide">
          <h2 class="home-auth-title">Session expired</h2>
          <p class="home-auth-subtitle">
            Your session has expired or you've been signed out. Sign in
            again to see this vault.
          </p>
          <a href="/" class="btn btn-primary btn-block">Sign in</a>
        </div>
      </div>
    );
  }
  if (error) return <div class="vault-detail"><p class="vault-error">{error}</p></div>;
  if (!vault) return <div class="vault-detail"><p class="vault-loading">Loading...</p></div>;

  const c = vault.contract;
  const state = vault.state.toUpperCase();
  const showProgressBar = progressStepIndex(state) >= 0;
  // ACCEPTED puts the deposit address inside the action card; every
  // other state exposes it under the Addresses disclosure.
  const showDepositInsideAddresses = state !== 'ACCEPTED';

  return (
    <div class="vault-detail">
      {/* Error banner for FAILED vaults */}
      {state === 'FAILED' && vault.error_message && (
        <div class="vault-error-banner">
          <strong>Vault failed</strong>
          <p>{vault.error_message}</p>
        </div>
      )}

      {state === 'ABANDONED' && (
        <div class="vault-error-banner vault-error-banner--warning">
          <strong>{vault.sub_status === 'refund_confirmed' ? 'Deposit returned' : 'Vault abandoned'}</strong>
          <p>
            {vault.sub_status === 'refund_confirmed'
              ? 'Your deposit was returned to your payout address.'
              : 'This vault was not funded within the required timeframe.'}
          </p>
        </div>
      )}

      {/* COB-396: the persisted counterparty relabels the Type row only —
          everything below (custody, addresses, withdrawal, verifier embed)
          deliberately keeps the Cobault frame. */}
      <ProductSubtitle payoutFormula={c.loss_formula} productco={vault.productco} />

      <div class="vault-header">
        <h1 class="vault-title">Your vault</h1>
      </div>
      <p class="vault-id mono">{vault.vault_id}</p>

      <VaultHero vault={vault} txs={vaultTxs} />

      <VaultActionCard
        vault={vault}
        session={session}
        embedAvailable={verifierOrigin !== null}
        onOpenVerifierConfirm={() => setShowVerifierConfirm(true)}
        withdrawalStep={withdrawalStep}
        withdrawalData={withdrawalData}
        withdrawalError={withdrawalError}
        onInitiate={async () => {
          if (!params.id) return;
          // Re-entrancy guard: once we've left 'idle' the request is
          // already in flight. The button is also disabled while
          // initiating; this closes the window belt-and-suspenders.
          if (withdrawalStep !== 'idle') return;
          setWithdrawalError(null);
          setWithdrawalStep('initiating');
          try {
            const resp = await api.initiateWithdrawal(params.id);
            setWithdrawalData(resp);
            setWithdrawalStep('reviewing');
          } catch (e) {
            setWithdrawalError(e instanceof Error ? e.message : 'Failed to initiate withdrawal');
            setWithdrawalStep('idle');
          }
        }}
        onSign={async () => {
          if (!params.id || !session.agent) return;
          const psbtHex = withdrawalData?.psbt_hex ?? vault.withdrawal_psbt_hex;
          if (!psbtHex) return;
          setWithdrawalError(null);
          setWithdrawalStep('signing');
          try {
            const psbtBytes = hexToBytes(psbtHex);
            // Pass the server-known derivation index so a fresh-device
            // session (no local index cache — the common Web3Auth
            // recovery case) can still derive the signing key.
            //
            // COB-178: also pass the vault's pinned destinations as an
            // anchor hint. A sealed anchor wins — since COB-462 that is the
            // normal case, sealed at contract acknowledgement. Otherwise this
            // hint heals it: it comes from the authenticated details response,
            // a different response than the PSBT, so a PSBT-only tamper is
            // caught — but it is NOT hash-verified in the browser (the SPA
            // never recomputes `contract_hash_hex` and never checks the
            // verifier's receipt signature), so a backend lying consistently
            // in both is caught arm's-length by the verifier, not here. The
            // agent refuses to sign (ClearSignError) if the PSBT pays a
            // destination the vault never agreed to or spends a non-vault
            // input.
            //
            // COB-491 (R5): and the verifier's attestation over this exact
            // transaction, plus the root-signed identity tuple to check it
            // against. This is what closes the gap the COB-178 comment above
            // names: a backend lying consistently in both the details response
            // and the PSBT now also has to forge an Ed25519 signature that
            // chains to a root pubkey baked into the bundle. Missing or
            // invalid ⇒ the agent refuses (ClearSignError) — including for a
            // SETTLING vault initiated before R5, which carries no attestation
            // and must re-initiate or drain.
            const signedBytes = await session.agent.signPsbt(
              params.id,
              psbtBytes,
              vault.derivation_index,
              {
                userPayoutAddress: c.user_payout_address,
                platformRefundAddress: c.platform_refund_address,
                // COB-245: deposit_address is optional in the API type now,
                // but this withdrawal path only runs for post-ACCEPTED states
                // where the address is always present; the sign-time heal path
                // re-pins from the authenticated contract if it were missing.
                depositAddress: vault.deposit_address ?? '',
              },
              withdrawalAttestationInput(vault, withdrawalData, verifierIdentity),
            );
            const signedHex = bytesToHex(signedBytes);
            setWithdrawalStep('broadcasting');
            await api.completeWithdrawal(params.id, { signed_psbt_hex: signedHex });
            setWithdrawalStep('done');
            fetchVault();
          } catch (e) {
            setWithdrawalError(e instanceof Error ? e.message : 'Withdrawal failed');
            setWithdrawalStep('reviewing');
          }
        }}
        onResume={() => {
          // Re-open the confirm/sign modal for a vault that's already had
          // its withdrawal initiated (PSBT built + stored, verifier-signed)
          // but not yet user-signed — e.g. after a page reload. No
          // re-initiate: the backend only allows EXPIRED→SETTLING once.
          setWithdrawalError(null);
          setWithdrawalStep('reviewing');
        }}
        onCancel={() => {
          // Close the modal without signing. The vault is already SETTLING
          // (initiate persisted the verifier-signed PSBT), so refresh to
          // surface the "review & sign" resume affordance rather than a
          // stale "Withdraw" button that would re-initiate and 400.
          setWithdrawalError(null);
          setWithdrawalStep('idle');
          fetchVault();
        }}
      />

      {/* COB-261 follow-up: independent-verifier confirmation, a first-class
          field on the page. For a not-yet-confirmed ACCEPTED vault this
          carries the confirm link — the deposit-protection gate the user must
          clear before an address is revealed. Hidden on terminal vaults,
          where it is historical noise. */}
      {!['CLOSED', 'ABANDONED', 'FAILED'].includes(state) && (
        <VerifierConfirmField
          acknowledged={vault.contract_acknowledged}
          verifierUrl={verifierVaultUrlForNetwork(session.bitcoinNetwork, vault.vault_id)}
          embedAvailable={verifierOrigin !== null}
          ctaAbove={state === 'ACCEPTED' && !vault.deposit_address && verifierOrigin !== null}
          revised={state !== 'ACCEPTED'}
          onConfirmInApp={() => setShowVerifierConfirm(true)}
        />
      )}

      {/* B1: under-deposit progress — only renders while FUNDING and the
          confirmed deposit is still below the contracted principal. */}
      {state === 'FUNDING' && <FundingActionCard vault={vault} txs={vaultTxs} />}

      {/* B2: return/cancel a partially-funded vault. Only offered once a
          (partial) deposit has actually confirmed — there has to be
          something to return — and disabled while a further inbound
          deposit is still confirming. The watcher refunds the deposit to
          the payout address and abandons the vault. */}
      {state === 'FUNDING' && confirmedDepositedSats(vaultTxs) > 0 && (
        <ReturnDepositSection
          vaultId={vault.vault_id}
          onReturned={fetchVault}
          pendingInbound={hasPendingInboundDeposit(vaultTxs)}
        />
      )}

      {showProgressBar && <VaultProgressBar vault={vault} />}

      <DealTermsDisclosure contract={c} />

      <AddressesDisclosure
        vault={vault}
        showDepositAddress={showDepositInsideAddresses}
      />

      {/* The protocol-internal block (block heights, contract hash,
          descriptor, verifier pubkey, receipt signature, payout formula,
          price source). Collapsed by default — it is reference material,
          not part of the live vault state above. */}
      <AdvancedDisclosure vault={vault} session={session} />

      {/* F18.3: Transactions live at the very bottom — they're
          history, not vault setup, so they're below Advanced rather
          than threaded into the live vault state above. */}
      {vaultTxs.length > 0 && <VaultTransactions txs={vaultTxs} />}

      {/* Offer re-approval modal */}
      {showOfferModal && freshOffer && vault && (
        <OfferReapprovalModal
          offer={freshOffer}
          contract={vault.contract}
          loading={offerModalLoading}
          errorMessage={offerModalError}
          onAccept={async () => {
            if (!params.id) return;
            setOfferModalError(null);
            setOfferModalLoading(true);
            try {
              await api.reacceptOffer(params.id, freshOffer);
              setSuppressAutoShowUntil(Date.now() + 15_000);
              setShowOfferModal(false);
              setFreshOffer(null);
              setOfferModalLoading(false);
              fetchVault();
              // COB-429 — the replaced contract needs the owner's
              // acknowledgement before the watcher will place the position, so
              // the ceremony runs in the same sitting rather than waiting for
              // them to find the field further down the page. Suppressed on an
              // unknown network, where the embed cannot be framed at all.
              if (verifierOrigin !== null) setShowVerifierConfirm(true);
            } catch (e) {
              setOfferModalError(e instanceof Error ? e.message : 'Failed to accept new offer.');
              setOfferModalLoading(false);
            }
          }}
          onReject={async () => {
            if (!params.id) return;
            setOfferModalError(null);
            setOfferModalLoading(true);
            try {
              await api.rejectOffer(params.id);
              setSuppressAutoShowUntil(Date.now() + 15_000);
              setShowOfferModal(false);
              setFreshOffer(null);
              setOfferModalLoading(false);
              fetchVault();
            } catch (e) {
              setOfferModalError(e instanceof Error ? e.message : 'Failed to reject offer.');
              setOfferModalLoading(false);
            }
          }}
        />
      )}

      {/* Pre-T32 B2: refund-in-progress modal */}
      {showRefundModal && (
        <RefundModal
          onAcknowledge={() => {
            if (vault) {
              localStorage.setItem(`vault-refund-acked-${vault.vault_id}`, '1');
            }
            setShowRefundModal(false);
          }}
        />
      )}

      {/* COB-263: verifier-served confirm embed. Advisory `confirmed` →
          close + refetch; the deposit-address reveal stays server-gated on
          the verifier's ack-status. */}
      {showVerifierConfirm && verifierOrigin && (
        <VerifierConfirmModal
          vaultId={vault.vault_id}
          verifierOrigin={verifierOrigin}
          onClose={() => setShowVerifierConfirm(false)}
          onConfirmed={() => {
            setShowVerifierConfirm(false);
            fetchVault();
          }}
        />
      )}
    </div>
  );
}

// ── VaultHero (Phase 5) ───────────────────────────────────────────────

function VaultHero({ vault, txs }: { vault: GetVaultDetailsResponse; txs: VaultTx[] }) {
  const countdownStr = useCountdown(vault.contract.settlement_reference_time_unix);
  const copy = vaultDetailHero({
    state: vault.state,
    principal_sats: vault.contract.principal_sats,
    reward_sats: vault.contract.reward_sats,
    user_sats: vault.user_sats,
    updated_at_unix: vault.updated_at_unix,
    now_unix: Math.floor(Date.now() / 1000),
    error_message: vault.error_message,
    sub_status: vault.sub_status,
    deposit_address_revealed: !!vault.deposit_address,
    countdownStr,
    pendingConfirmations:
      inflightConfirmations(
        vault.state,
        vault.sub_status,
        txs,
        vault.required_confirmations,
      ) ?? undefined,
  });
  return (
    <div class="vault-hero">
      {/* COB-261: the principal reads as the page's hero figure — a big
          Fraunces display number with a small unit, mirroring the wallet
          balance and the withdrawal "you receive" figure. The state copy
          below names it (e.g. "Your 0.05 BTC is earning yield."), so the
          figure + sentence read as a label/value pair rather than chrome. */}
      <div class="vault-hero-figure tabular-nums">
        {formatBtc(vault.contract.principal_sats)}
        <span class="vault-hero-unit">BTC</span>
      </div>
      <div class="vault-hero-primary">{copy.primary}</div>
      <div class="vault-hero-secondary tabular-nums">{copy.secondary}</div>
    </div>
  );
}

// ── Verifier confirmation (COB-261 follow-up) ─────────────────────────
//
// The independent-verifier confirmation is a first-class field on the page,
// never buried under a disclosure. When the contract has NOT yet been
// confirmed — an ACCEPTED vault before its deposit address is revealed, which
// deposit-protection primacy makes the load-bearing step — this field IS the
// link to the verifier's own confirm surface. Once confirmed it reads as a
// done indicator. The "Verify independently" link under Advanced options, the
// verifier-spa's own confirm/record flow, and the
// quit-accept-visit-verifier-directly path are all separate paths to the same
// place.
function VerifierConfirmField({
  acknowledged,
  verifierUrl,
  embedAvailable,
  ctaAbove,
  revised,
  onConfirmInApp,
}: {
  acknowledged: boolean;
  verifierUrl: string | null;
  /** COB-263: whether the verifier-served confirm embed can run on this
   *  network (verifier origin known). When true, offer the in-app confirm
   *  and keep the direct link as the escape hatch. */
  embedAvailable: boolean;
  /** COB-701: the ACCEPTED action card at the top of the page already carries
   *  the in-app confirm button while the address is withheld. Don't render the
   *  same primary button twice on one screen — point at it and keep the direct
   *  verifier link as the escape hatch. */
  ctaAbove: boolean;
  /** COB-429: past ACCEPTED an unacknowledged contract can only mean a
   *  reaccept replaced it — the vault is funded and held until the owner
   *  confirms the REVISED contract, so the field says so in those words. */
  revised: boolean;
  onConfirmInApp: () => void;
}) {
  const showButton = embedAvailable && !ctaAbove;
  const linkLabel = !embedAvailable
    ? 'Confirm on the Cobault Verifier →'
    : showButton
      ? 'Or open the verifier directly ↗'
      : 'Open the verifier directly ↗';
  return (
    <section class="vault-section vault-verifier-confirm">
      <h2 class="vault-section-title">Independent verifier</h2>
      {acknowledged ? (
        <p class="vault-verifier-confirm-done">
          <span class="vault-verifier-confirm-check" aria-hidden="true">✓</span>
          Contract confirmed with the Cobault Verifier.
        </p>
      ) : (
        <>
          <p class="vault-verifier-confirm-cta">
            {revised
              ? 'Your revised contract is registered with the independent verifier but not yet confirmed. Confirm it to resume the vault — nothing is traded until you do.'
              : ctaAbove
                ? 'Not yet confirmed. Use the button above to confirm the registered contract before you deposit.'
                : 'Confirm the registered contract on the independent verifier before you deposit.'}
          </p>
          {/* COB-263: confirm in-place via the verifier-served embed (its own
              origin renders the contract). The direct link is retained as the
              escape-hatch path. */}
          {showButton && (
            <button
              type="button"
              class="btn btn-primary vault-verifier-confirm-btn"
              onClick={onConfirmInApp}
            >
              {revised ? 'Confirm your revised contract' : 'Confirm your contract'}
            </button>
          )}
          {verifierUrl && (
            <p class="vault-verifier-confirm-alt">
              <a
                class="vault-field-link"
                href={verifierUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {linkLabel}
              </a>
            </p>
          )}
        </>
      )}
    </section>
  );
}

// ── VaultActionCard (Phase 5) ─────────────────────────────────────────
//
// Single primary CTA for ACCEPTED + EXPIRED, settlement-summary tile
// for SETTLED+CLOSED. All other states render nothing — the hero
// carries the page. EXPIRED hosts the multi-step withdrawal flow
// (idle → initiating → reviewing → signing → broadcasting → done);
// once the watcher transitions the vault into SETTLING (PSBT stored)
// the same review/sign UI continues to surface here so the flow
// survives a page reload.

interface VaultActionCardProps {
  vault: GetVaultDetailsResponse;
  session: ReturnType<typeof useSession>;
  /** COB-263: whether the verifier confirm embed can run (verifier origin
   *  known for this network). */
  embedAvailable: boolean;
  /** COB-263: open the verifier-served confirm modal. */
  onOpenVerifierConfirm: () => void;
  withdrawalStep: WithdrawalStep;
  withdrawalData: InitiateWithdrawalResponse | null;
  withdrawalError: string | null;
  onInitiate: () => void;
  onSign: () => void;
  onResume: () => void;
  onCancel: () => void;
}

function VaultActionCard(props: VaultActionCardProps) {
  const { vault } = props;
  const state = vault.state.toUpperCase();
  if (state === 'ACCEPTED') {
    return (
      <DepositActionCard
        vault={vault}
        embedAvailable={props.embedAvailable}
        onOpenVerifierConfirm={props.onOpenVerifierConfirm}
      />
    );
  }
  if (state === 'EXPIRED' || state === 'SETTLING' || state === 'SETTLED' || state === 'CLOSED') {
    return <WithdrawalActionCard {...props} />;
  }
  return null;
}

function DepositActionCard({
  vault,
  embedAvailable,
  onOpenVerifierConfirm,
}: {
  vault: GetVaultDetailsResponse;
  embedAvailable: boolean;
  onOpenVerifierConfirm: () => void;
}) {
  const principalBtc = formatBtc(vault.contract.principal_sats);
  // COB-245: the deposit address is revealed only after the owner confirms the
  // registered contract on the independent verifier (`contract_acknowledged`).
  // Until then show the confirmation step instead of a funding address — this
  // is the deposit-protection gate: no funds move before independent review.
  if (!vault.deposit_address) {
    return (
      <section class="vault-action-card">
        <h2 class="vault-action-card-title">Confirm your vault contract</h2>
        <p class="vault-action-card-hint">
          To protect your deposit, confirm the registered contract on the independent verifier
          before funding. Your deposit address is revealed here once you have confirmed.
        </p>
        {/* COB-263: confirm in-place via the verifier-served embed. */}
        {embedAvailable && (
          <button
            type="button"
            class="btn btn-primary"
            onClick={onOpenVerifierConfirm}
          >
            Confirm your contract
          </button>
        )}
      </section>
    );
  }
  return (
    <section class="vault-action-card">
      <h2 class="vault-action-card-title">Send exactly {principalBtc} BTC</h2>
      <DepositAddressField address={vault.deposit_address} />
      <p class="vault-action-card-hint">
        Vault starts as soon as the deposit confirms on-chain.
        Over- or under-deposits refund to your payout address.
      </p>
    </section>
  );
}

/** FUNDING-state card. The watcher moves a vault to FUNDING the moment it sees
 *  *any* deposit UTXO — confirmed or not — so this card distinguishes three
 *  cases (see `fundingDepositStatus`):
 *    - the full deposit is detected but still confirming → reassure, no address;
 *    - it's genuinely short → show how much arrived + the address to top up;
 *    - it's fully confirmed → render nothing (the hero's confirmation counter
 *      carries the page). */
function FundingActionCard({ vault, txs }: { vault: GetVaultDetailsResponse; txs: VaultTx[] }) {
  const principalBtc = formatBtc(vault.contract.principal_sats);
  const status = fundingDepositStatus(vault.contract.principal_sats, txs);

  if (status.kind === 'complete') return null;

  if (status.kind === 'detected') {
    // The remaining amount is already in the mempool — detected, just not yet
    // confirmed. Reassure rather than telling the user to send more.
    const detectedHint =
      status.confirmed > 0
        ? `We’ve detected the remaining ${formatBtc(status.pending)} BTC of your deposit. Your vault will start automatically once it confirms on-chain — nothing more to send.`
        : `We’ve detected your ${formatBtc(status.pending)} BTC deposit. Your vault will start automatically once it confirms on-chain — nothing more to send.`;
    return (
      <section class="vault-action-card">
        <h2 class="vault-action-card-title">Deposit detected — confirming</h2>
        <p class="vault-action-card-hint tabular-nums">{detectedHint}</p>
      </section>
    );
  }

  // Genuinely short, even counting anything in-flight: show how much has
  // arrived, note any in-flight top-up, and surface the address to complete it.
  const incompleteHint =
    status.pending > 0
      ? `Received ${formatBtc(status.confirmed)} of ${principalBtc} BTC, with ${formatBtc(status.pending)} BTC more detected and confirming. Send ${formatBtc(status.stillToSend)} BTC more to the deposit address to start the vault.`
      : `Received ${formatBtc(status.confirmed)} of ${principalBtc} BTC. Send ${formatBtc(status.stillToSend)} BTC more to the deposit address to start the vault.`;
  return (
    <section class="vault-action-card">
      <h2 class="vault-action-card-title">Deposit incomplete</h2>
      <p class="vault-action-card-hint tabular-nums">{incompleteHint}</p>
      <DepositAddressField address={vault.deposit_address} />
    </section>
  );
}

/** B2: user-initiated "return deposit" / cancel for a pre-funded vault
 *  (ACCEPTED or FUNDING). The watcher returns any deposit to the payout
 *  address (Platform + Verifier co-sign — no user key) and abandons the
 *  vault. Two-tap confirm so it can't fire on a stray click. */
function ReturnDepositSection({
  vaultId,
  onReturned,
  pendingInbound,
}: {
  vaultId: string;
  onReturned: () => void;
  /** A further deposit is detected but not yet confirmed — the control
   *  is shown but inactive until it settles (avoids racing the inbound
   *  UTXO with the refund). */
  pendingInbound: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      await api.returnDeposit(vaultId);
      setConfirming(false);
      onReturned();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to return deposit.');
    } finally {
      setBusy(false);
    }
  };

  if (!confirming) {
    return (
      <div class="vault-return">
        <button
          type="button"
          class="vault-return-link"
          disabled={pendingInbound}
          onClick={() => setConfirming(true)}
        >
          Return deposit &amp; cancel vault
        </button>
        {pendingInbound && (
          <p class="vault-return-hint">
            Waiting for the incoming deposit to confirm before this can be
            returned.
          </p>
        )}
      </div>
    );
  }
  return (
    <div class="vault-return vault-return--confirming">
      <p class="vault-return-prompt">
        Return any deposited funds to your payout address and cancel this vault?
        This can&rsquo;t be undone.
      </p>
      {error && <p class="vault-return-error">{error}</p>}
      {pendingInbound && (
        <p class="vault-return-hint">
          A new deposit is still confirming — returning is paused until it
          settles.
        </p>
      )}
      <div class="vault-return-actions">
        <button
          type="button"
          class="vault-return-confirm"
          disabled={busy || pendingInbound}
          onClick={submit}
        >
          {busy ? 'Returning…' : 'Yes, return my deposit'}
        </button>
        <button
          type="button"
          class="vault-return-cancel"
          disabled={busy}
          onClick={() => {
            setConfirming(false);
            setError(null);
          }}
        >
          Keep vault
        </button>
      </div>
    </div>
  );
}

function WithdrawalActionCard(props: VaultActionCardProps) {
  const {
    vault, session, withdrawalStep, withdrawalData, withdrawalError,
    onInitiate, onSign, onResume, onCancel,
  } = props;
  const state = vault.state.toUpperCase();
  // The confirm/sign step runs in an overlay modal (case 'modal' below),
  // replacing the old inline review card so the busy ("Signing…") state is
  // unmissable. View routing lives in the pure, unit-tested helper.
  const view = withdrawalCardView(state, withdrawalStep, vault);

  // 1. Active flow → overlay modal.
  if (view === 'modal') {
    return (
      <WithdrawalModal
        vault={vault}
        session={session}
        withdrawalStep={withdrawalStep}
        withdrawalData={withdrawalData}
        withdrawalError={withdrawalError}
        onSign={onSign}
        onCancel={onCancel}
      />
    );
  }

  // 2. Terminal settlement summary wins over any lingering step.
  if (view === 'summary') {
    const userSats = withdrawalData?.user_sats ?? vault.user_sats;
    const platformSats = withdrawalData?.platform_sats ?? vault.platform_sats;
    const priceCents = withdrawalData?.settlement_price_cents ?? vault.settlement_price_cents;
    if (priceCents == null && userSats == null && !vault.withdrawal_txid) return null;
    return (
      <section class="vault-action-card vault-action-card--summary">
        <h2 class="vault-action-card-title">Settlement</h2>
        <div class="vault-grid">
          {priceCents != null && (
            <VaultField label="Settlement price" value={formatUsd(priceCents / 100)} tabular />
          )}
          {userSats != null && (
            <VaultField label="You received" value={`${formatBtc(userSats)} BTC`} tabular />
          )}
          {platformSats != null && platformSats > 0 && (
            <VaultField
              label="Platform received"
              value={`${formatBtc(platformSats)} BTC`}
              tabular
            />
          )}
        </div>
        {vault.withdrawal_txid && (
          <div class="vault-grid vault-grid-single">
            <VaultField label="Withdrawal txid" value={vault.withdrawal_txid} mono />
          </div>
        )}
      </section>
    );
  }

  // 3. Needs the user's signature: EXPIRED first run (the "Withdraw" CTA),
  //    or a reload landing mid-flow after initiate (PSBT stored, not yet
  //    user-signed → "Review & sign" resume). The 'initiating' step keeps
  //    this card mounted and greys the button so the click gets immediate,
  //    button-anchored feedback and can't be re-fired while in flight.
  if (view === 'needs-signature') {
    const isInitiating = withdrawalStep === 'initiating';
    // A SETTLING vault here has already had its PSBT built + stored
    // (verifier-signed) — resume straight into the modal rather than
    // re-initiating, which the backend would reject. EXPIRED is first-run.
    const resuming = state === 'SETTLING';
    return (
      <section class="vault-action-card">
        <h2 class="vault-action-card-title">Ready to settle</h2>
        <p class="vault-action-card-desc">
          {isInitiating
            ? 'Fetching the settlement price and constructing the withdrawal transaction.'
            : 'Sign once to settle and receive your principal + reward at the current settlement price.'}
        </p>
        {withdrawalError && <p class="vault-error">{withdrawalError}</p>}
        <button
          class="btn btn-primary btn-block"
          onClick={resuming ? onResume : onInitiate}
          disabled={isInitiating}
        >
          {isInitiating
            ? 'Preparing…'
            : resuming
              ? 'Review & sign withdrawal'
              : 'Withdraw'}
        </button>
      </section>
    );
  }

  // 4. Pending withdrawal: the user has signed and the watcher is co-signing
  //    + broadcasting. Covers the transient 'done' step and any later reload
  //    while SETTLING with the post-sign sub_status.
  if (view === 'pending') {
    return (
      <section class="vault-action-card">
        <h2 class="vault-action-card-title">Withdrawal submitted</h2>
        <p class="vault-action-card-desc">Awaiting on-chain confirmation.</p>
      </section>
    );
  }

  return null;
}

// ── Withdrawal confirm/sign modal ─────────────────────────────────────

/** Overlay modal for the confirm + sign step of a withdrawal. Two visual
 *  states: a confirmation showing the settlement preview + payout address
 *  with Sign / Cancel, and a non-dismissable "Signing…" state while the
 *  key-agent signs and the signed PSBT is submitted. Replaces the old
 *  inline review card (and its address-confirm checkbox): the prominent
 *  payout address re-display + the conscious Sign action (with a Cancel →
 *  contact-support escape) preserves the S05.7 substitution check without
 *  the redundant checkbox. */
/** Neutral label classifying a decoded output address against the vault's
 *  known destinations. Returns a plain descriptor only — never a safety
 *  claim and never a "verified" tick (a compromised app could forge it).
 *  An output that matches none of them (which the agent's
 *  assertWithdrawalSafe would refuse at sign time) is surfaced honestly so
 *  the user can catch it and cancel. */
export function decodedDestinationLabel(
  address: string | null,
  c: ContractResponse,
  feeChangeAddrs: Set<string>,
): string {
  if (!address) return 'a non-standard script';
  if (address === c.user_payout_address) return 'your payout address';
  if (address === c.platform_refund_address) return 'the platform refund address';
  if (feeChangeAddrs.has(address)) return 'network-fee change';
  return 'an unrecognized address';
}

/**
 * COB-491 (R5): assemble what the key agent needs to verify the verifier's
 * attestation over the withdrawal it is about to sign.
 *
 * Two sources, deliberately in this order:
 *   * `withdrawalData.signed_withdrawal_attestation` — the initiate response,
 *     the path a user who never left the page takes;
 *   * `vault.withdrawal_attestation_json` — the persisted copy served on
 *     `GET /vault/{id}/details`, which is the ONLY source on the **resume**
 *     path (post-reload the review step re-enters from persisted state with no
 *     initiate response in hand).
 *
 * Both are relayed opaquely: this function never inspects the envelope, and the
 * SPA never verifies it. Verification belongs to the agent, against the root
 * pubkey compiled into its bundle — not against anything user-app asserts,
 * which is why the identity tuple travels alongside rather than being trusted
 * here.
 *
 * Exported for direct unit testing of the source precedence.
 */
export function withdrawalAttestationInput(
  vault: GetVaultDetailsResponse,
  withdrawalData: InitiateWithdrawalResponse | null,
  identity: VerifierIdentity,
): WithdrawalAttestationInput {
  return {
    envelope:
      withdrawalData?.signed_withdrawal_attestation ??
      vault.withdrawal_attestation_json ??
      null,
    identity,
    contractHashHex: vault.contract_hash_hex,
  };
}

function WithdrawalModal({
  vault, session, withdrawalStep, withdrawalData, withdrawalError, onSign, onCancel,
}: {
  vault: GetVaultDetailsResponse;
  session: ReturnType<typeof useSession>;
  withdrawalStep: WithdrawalStep;
  withdrawalData: InitiateWithdrawalResponse | null;
  withdrawalError: string | null;
  onSign: () => void;
  onCancel: () => void;
}) {
  const c = vault.contract;
  const verifierIdentity = useVerifierIdentity();
  const isBusy = withdrawalStep === 'signing' || withdrawalStep === 'broadcasting';
  const platformSats = withdrawalData?.platform_sats ?? vault.platform_sats;
  const priceCents = withdrawalData?.settlement_price_cents ?? vault.settlement_price_cents;

  // COB-178 (R1): decode the actual PSBT the user is about to sign so the
  // modal shows the real outputs/inputs, not just server-supplied JSON.
  // The hard destination/ownership block lives in the key agent's
  // signPsbt (R2/R3); this is its human-readable companion.
  //
  // The decode is *mandatory*: if it fails (or hasn't completed) the Sign
  // button below is disabled and a blocked/loading state is shown, so the
  // user can never sign a withdrawal we couldn't render to them. This adds
  // no new liveness failure — a PSBT inspectPsbt can't decode is one
  // assertWithdrawalSafe would also refuse at sign time (same
  // Transaction.fromPSBT) — it just surfaces the "no" earlier and legibly.
  const psbtHex = withdrawalData?.psbt_hex ?? vault.withdrawal_psbt_hex;
  const [decoded, setDecoded] = useState<PsbtInspection | null>(null);
  const [decodeStatus, setDecodeStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  useEffect(() => {
    let cancelled = false;
    setDecoded(null);
    setDecodeStatus('loading');
    // While signing/broadcasting the modal shows the busy spinner instead,
    // so the decode state is not read in that branch.
    if (isBusy) return;
    if (!psbtHex || !session.agent) {
      setDecodeStatus('error');
      return;
    }
    const agent = session.agent;
    agent
      .inspectPsbt(hexToBytes(psbtHex))
      .then((d) => {
        if (cancelled) return;
        if (d.outputs.length === 0) {
          setDecodeStatus('error');
          return;
        }
        setDecoded(d);
        setDecodeStatus('ok');
      })
      .catch(() => { if (!cancelled) setDecodeStatus('error'); });
    return () => { cancelled = true; };
  }, [psbtHex, isBusy, session.agent]);

  // COB-491 (R5): run the same check `signPsbt` will run, so the amounts can
  // say whether the arm's-length verifier attested them. Strictly a LABEL —
  // the control is the agent's own assertion inside `signPsbt`, which re-runs
  // this and refuses regardless of what was rendered here. Deliberately not a
  // gate on the Sign button either: the existing decode/payout gates keep
  // their exact semantics (COB-178), and an unattested withdrawal surfaces its
  // refusal from the agent, in the agent's own words.
  const attestationInput = withdrawalAttestationInput(vault, withdrawalData, verifierIdentity);
  const [attested, setAttested] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setAttested(false);
    if (isBusy || !psbtHex || !session.agent) return;
    const agent = session.agent;
    agent
      .checkWithdrawalAttestation(vault.vault_id, hexToBytes(psbtHex), attestationInput)
      .then((res) => {
        if (!cancelled) setAttested(res.ok);
      })
      .catch(() => { if (!cancelled) setAttested(false); });
    return () => { cancelled = true; };
    // `attestationInput` is a fresh object every render, so it is deliberately
    // not a dependency — its value is fully determined by the four below (the
    // envelope travels with the PSBT, the contract hash with the vault).
  }, [psbtHex, isBusy, session.agent, verifierIdentity.verifier_identity_pubkey_hex]);

  // Non-multisig inputs are the hot-wallet fee input(s); an output paying
  // back to one of their scripts is fee change, not an external send. Used
  // only to label outputs neutrally below.
  const feeChangeAddrs = new Set(
    (decoded?.inputs ?? [])
      .filter((inp) => !inp.isMultisig && inp.address)
      .map((inp) => inp.address as string),
  );

  // The headline amount comes from the DECODED output(s) paying the user's
  // pinned payout address — WYSIWYS, not server JSON. payoutMissing flags
  // the case (which assertWithdrawalSafe's destination-only R2 would NOT
  // catch — R4 amount recompute is deferred) where the tx pays the user
  // nothing: we surface it loudly and force the detail open rather than
  // showing a reassuring headline.
  const payoutSats = (decoded?.outputs ?? [])
    .filter((o) => o.address === c.user_payout_address)
    .reduce((sum, o) => sum + o.sats, 0);
  const payoutMissing = decodeStatus === 'ok' && payoutSats === 0;

  return (
    <div class="vault-modal-overlay">
      <div
        class="vault-modal vault-modal--withdrawal"
        onClick={(e) => e.stopPropagation()}
      >
        {isBusy ? (
          <div class="withdrawal-signing">
            <div class="withdrawal-spinner" aria-hidden="true" />
            <p class="withdrawal-signing-text">
              {withdrawalStep === 'broadcasting' ? 'Broadcasting…' : 'Signing withdrawal…'}
            </p>
            <p class="withdrawal-signing-sub">
              This only takes a moment — please keep this window open.
            </p>
          </div>
        ) : (
          <>
            <div class="withdrawal-modal-head">
              <span class="withdrawal-modal-eyebrow">
                <span class="withdrawal-modal-diamonds" aria-hidden="true">
                  ◇◆◇
                </span>
                2-of-3 · you sign with your key
              </span>
              <button
                type="button"
                class="withdrawal-modal-close"
                aria-label="Cancel withdrawal"
                onClick={onCancel}
              >
                ×
              </button>
            </div>
            <h2 class="vault-modal-title">Confirm withdrawal</h2>
            <p class="vault-modal-desc">
              Review the details below, then sign to settle and receive your funds.
            </p>
            {/* COB-178 (R1): decode is mandatory — a loading/blocked state
                shows until inspectPsbt succeeds, and the Sign button is
                gated on it (below). The common case is kept to the one thing
                that matters — what you receive and where (sourced from the
                DECODED PSBT, not server JSON) — with the full breakdown
                (inputs, platform/fee outputs, settlement price) tucked under
                "More details" so users aren't hit with a wall of data. */}
            {decodeStatus === 'loading' && (
              <p class="withdrawal-confirm-note">
                Decoding the transaction you&rsquo;re about to sign…
              </p>
            )}
            {decodeStatus === 'error' && (
              <p class="vault-modal-error">
                We couldn&rsquo;t decode this transaction to show you exactly
                what you&rsquo;d be signing, so signing is blocked for your
                safety. Please cancel and contact support.
              </p>
            )}
            {decodeStatus === 'ok' && decoded && (
              <>
                {payoutMissing ? (
                  <p class="vault-modal-error">
                    This transaction does not pay your payout address. Do not
                    sign — cancel and contact support.
                  </p>
                ) : (
                  <>
                    {/* Headline: WYSIWYS amount (decoded) shown as the big
                        Fraunces figure + the pinned payout address
                        re-displayed at signing time (S05.7), recessed into
                        an --ink inset box. The neutral "your payout address"
                        label is a statement of which contract field this is,
                        not a safety tick (COB-178) — the warning below tells
                        the user to verify it themselves. */}
                    <div class="withdrawal-receive">
                      <div class="withdrawal-receive-label">
                        <span>You receive</span>
                        {/* COB-491 (R5): shown only when the verifier's signed
                            attestation over THIS transaction verified against
                            the root-signed identity chain. Unlike the
                            destination labels below, this one is a claim about
                            an independent party's signature, not about our own
                            state — which is exactly why it is allowed to say
                            "verified" where they are not. */}
                        {attested && (
                          <span class="withdrawal-attested" title="The independent verifier signed a statement covering these exact amounts and destinations, and that signature checked out in your browser.">
                            ◆ verifier-attested
                          </span>
                        )}
                      </div>
                      <div class="withdrawal-receive-figure tabular-nums">
                        {formatBtc(payoutSats)}
                        <span class="withdrawal-receive-unit">BTC</span>
                      </div>
                    </div>
                    <div class="withdrawal-payout">
                      <div class="withdrawal-payout-label">
                        To your payout address
                      </div>
                      <div class="withdrawal-payout-box mono">
                        {c.user_payout_address}
                      </div>
                    </div>
                  </>
                )}
                <details
                  class="vault-section-collapsible withdrawal-more-details"
                  open={payoutMissing}
                >
                  <summary class="vault-section-title">
                    <span class="vault-section-collapsible-chevron" aria-hidden="true">▸</span>
                    <span>More details — decoded transaction</span>
                  </summary>
                  <div class="vault-collapsible-body">
                    <div class="vault-grid">
                      {priceCents != null && (
                        <VaultField
                          label="Settlement price"
                          value={formatUsd(priceCents / 100)}
                          tabular
                        />
                      )}
                      {platformSats != null && platformSats > 0 && (
                        <VaultField
                          label="Platform receives"
                          value={`${formatBtc(platformSats)} BTC`}
                          tabular
                        />
                      )}
                    </div>
                    {/* Full decoded inputs/outputs from the PSBT bytes —
                        full addresses (no truncation), neutral non-asserting
                        labels (no "verified" tick). The load-bearing
                        destination control is the arm's-length verifier + the
                        agent's fail-closed assertWithdrawalSafe; this is the
                        human-readable companion. */}
                    <div class="vault-grid vault-grid-single">
                      <div class="vault-field">
                        <div class="vault-field-label">
                          Decoded from the transaction you&rsquo;re signing
                        </div>
                        <div class="vault-field-value">
                          {decoded.inputs
                            .filter((inp) => inp.isMultisig)
                            .map((inp, i) => (
                              <div key={`in-${i}`} class="withdrawal-decoded-in">
                                Spending vault UTXO {truncate(inp.txid, 16)}:{inp.vout}
                                {inp.sats != null ? ` (${formatBtc(inp.sats)} BTC)` : ''}
                              </div>
                            ))}
                          {decoded.outputs.map((o, i) => (
                            <div key={`out-${i}`} class="withdrawal-decoded-out">
                              <div class="withdrawal-decoded-summary">
                                → {formatBtc(o.sats)} BTC to{' '}
                                <span class="withdrawal-decoded-label">
                                  {decodedDestinationLabel(o.address, c, feeChangeAddrs)}
                                </span>
                              </div>
                              <div class="withdrawal-decoded-addr mono">
                                {o.address ?? 'non-standard script'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </details>
              </>
            )}
            {decodeStatus === 'ok' && !payoutMissing && (
              <p class="withdrawal-payout-warning">
                <span class="withdrawal-payout-warning-icon" aria-hidden="true">
                  ⚠
                </span>
                <span>
                  Check the payout address above is a wallet you control — this
                  is final and can&rsquo;t be reversed once broadcast. If
                  anything looks wrong, cancel and contact support.
                </span>
              </p>
            )}
            {withdrawalError && <p class="vault-modal-error">{withdrawalError}</p>}
            {!session.isLoggedIn && (
              <p class="withdrawal-confirm-note">
                You need to be logged in to sign this withdrawal.
              </p>
            )}
            <div class="vault-modal-actions">
              <button class="btn btn-secondary" onClick={onCancel}>
                Cancel
              </button>
              {session.isLoggedIn && (
                <button
                  class="btn btn-primary"
                  onClick={onSign}
                  disabled={decodeStatus !== 'ok' || payoutMissing}
                >
                  Sign &amp; settle
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── VaultProgressBar (Phase 5) ────────────────────────────────────────

function VaultProgressBar({ vault }: { vault: GetVaultDetailsResponse }) {
  const state = vault.state.toUpperCase();
  const activeIdx = progressStepIndex(state);
  const isActionRequired = vault.sub_status === 'offer_invalid';
  const activeDescription = activeIdx < PROGRESS_STEPS.length
    ? progressStepDescription(state, vault.sub_status)
    : '';

  return (
    <div class="vault-progress">
      <ol class="vault-progress-steps">
        {PROGRESS_STEPS.map((step, idx) => {
          let status: 'completed' | 'active' | 'pending';
          if (idx < activeIdx) status = 'completed';
          else if (idx === activeIdx) status = 'active';
          else status = 'pending';
          const isAction = status === 'active' && isActionRequired;
          return (
            <li
              key={step.key}
              class={`vault-progress-step vault-progress-step--${status}${isAction ? ' vault-progress-step--action' : ''}`}
            >
              <span class="vault-progress-step-marker" aria-hidden="true" />
              <span class="vault-progress-step-label">{step.label}</span>
            </li>
          );
        })}
      </ol>
      {activeDescription && (
        <p
          class={`vault-progress-active-desc${isActionRequired ? ' vault-progress-active-desc--action' : ''}`}
        >
          {activeDescription}
        </p>
      )}
    </div>
  );
}

// ── Disclosures (Phase 5) ─────────────────────────────────────────────

function DealTermsDisclosure({ contract }: { contract: ContractResponse }) {
  const settlementTime = contract.settlement_reference_time_unix;
  return (
    <details class="vault-section-collapsible" open>
      <summary class="vault-section-title">
        <span class="vault-section-collapsible-chevron" aria-hidden="true">▸</span>
        <span>Deal terms</span>
      </summary>
      <div class="vault-collapsible-body">
        <div class="vault-grid">
          <VaultField label="Principal" value={`${formatBtc(contract.principal_sats)} BTC`} tabular />
          <VaultField label="Reward" value={`${formatBtc(contract.reward_sats)} BTC`} tabular />
          <VaultField label="Max loss" value={formatPercent(contract.max_loss_bps / 10000)} tabular />
          <VaultField
            label="Settlement time"
            value={new Date(settlementTime * 1000).toISOString().replace('T', ' ').slice(0, 19) + ' UTC'}
          />
          <VaultField label="Price cap (at)" value={formatUsd(contract.strikes.short_usd)} tabular />
          <VaultField label="Loss protection (from)" value={formatUsd(contract.strikes.long_usd)} tabular />
        </div>
      </div>
    </details>
  );
}

function AddressesDisclosure({ vault, showDepositAddress }: {
  vault: GetVaultDetailsResponse;
  showDepositAddress: boolean;
}) {
  const c = vault.contract;
  return (
    <details class="vault-section-collapsible" open>
      <summary class="vault-section-title">
        <span class="vault-section-collapsible-chevron" aria-hidden="true">▸</span>
        <span>Addresses</span>
      </summary>
      <div class="vault-collapsible-body">
        {showDepositAddress && (
          <div class="vault-grid vault-grid-single">
            <DepositAddressField address={vault.deposit_address} />
          </div>
        )}
        <div class="vault-grid vault-grid-single">
          <VaultField label="Payout address" value={c.user_payout_address} mono yours />
          <p class="vault-field-hint">
            Payouts will be made to this address only. Double-check it
            before sending BTC or signing a withdrawal.
          </p>
        </div>
      </div>
    </details>
  );
}

function AdvancedDisclosure({ vault, session }: {
  vault: GetVaultDetailsResponse;
  session: ReturnType<typeof useSession>;
}) {
  const c = vault.contract;
  const verifierUrl = verifierVaultUrlForNetwork(session.bitcoinNetwork, vault.vault_id);
  return (
    <details class="vault-section-collapsible">
      <summary class="vault-section-title">
        <span class="vault-section-collapsible-chevron" aria-hidden="true">▸</span>
        <span>Advanced options</span>
      </summary>
      <div class="vault-collapsible-body">
        <div class="vault-grid">
          <VaultField label="Start block" value={c.term_start_block.toLocaleString()} tabular />
          <VaultField label="End block" value={c.term_end_block.toLocaleString()} tabular />
          <VaultField
            label="Refund unlock block"
            value={c.refund_unlock_block.toLocaleString()}
            tabular
          />
          <VaultField label="Payout formula" value={c.loss_formula} />
          <VaultField label="Price source" value={c.price_source_id} />
        </div>
        <div class="vault-grid vault-grid-single">
          <VaultField label="Contract hash" value={vault.contract_hash_hex} mono />
          {/* V08b: link into the independent verifier's public view for
           *  this vault. The user can hand-compare the contract hash
           *  above with the hash the verifier computes, and (via the
           *  Stage-2 flow) prove ownership with one click back into
           *  this origin's key-agent. Hidden on unknown networks so we
           *  never point at a wrong host. */}
          {verifierUrl && (
            <p class="vault-field-hint">
              <a
                class="vault-field-link"
                href={verifierUrl}
                target="_blank"
                rel="noopener"
              >
                Verify independently on the Cobault Verifier →
              </a>
            </p>
          )}
          <VaultField label="Multisig descriptor" value={c.multisig_descriptor} mono />
          <p class="vault-field-hint">
            Keys inside <code>multi(2,…)</code> appear in this order: your key,
            platform key, verifier key. The refund branch <code>after(…)</code> is
            signed by your refund key, unlocking after the stated block height.
          </p>
          <VaultField label="Verifier pubkey" value={vault.verifier_multisig_pubkey_hex} mono />
          <VaultField label="Receipt signature" value={vault.signed_receipt.signature_base64} mono />
        </div>
      </div>
    </details>
  );
}

// ── Offer re-approval modal ───────────────────────────────────────────

function OfferReapprovalModal({ offer, contract, loading, errorMessage, onAccept, onReject }: {
  offer: OfferResponse;
  contract: ContractResponse;
  loading: boolean;
  errorMessage: string | null;
  onAccept: () => void;
  onReject: () => void;
}) {
  // Derive original terms from the contract for comparison.
  const origReturn = contract.reward_sats / contract.principal_sats;
  const origMaxLoss = contract.max_loss_bps / 10_000;
  const origShortStrike = contract.strikes.short_usd;
  const origLongStrike = contract.strikes.long_usd;
  const origMaturity = formatDate(contract.settlement_reference_time_unix);

  // F16: per-period reward = yield_apy × days/365 (mirrors backend
  // `parse_offer_terms`). Comparing against the locked-in
  // `origReturn = reward_sats / principal_sats` lets us flag
  // a meaningful change at re-acceptance.
  const newReturn = periodRewardFraction(offer.economics.yield_apy, offer.days_to_expiry);
  const newMaxLoss = offer.economics.max_loss_pct;
  const newShortStrike = Math.round(offer.short_leg.strike);
  const newLongStrike = Math.round(offer.long_leg.strike);

  // Show "was X" when the value has meaningfully changed.
  const returnChanged = Math.abs(newReturn - origReturn) > 0.0001;
  const maxLossChanged = Math.abs(newMaxLoss - origMaxLoss) > 0.0001;
  const shortChanged = newShortStrike !== origShortStrike;
  const longChanged = newLongStrike !== origLongStrike;
  const maturityChanged = offer.maturity_date !== origMaturity;

  return (
    <div class="vault-modal-overlay">
      <div class="vault-modal" onClick={(e) => e.stopPropagation()}>
        <h2 class="vault-modal-title">Offer expired</h2>
        <p class="vault-modal-desc">
          Your original offer has expired. Accept the current offer below to continue, or
          reject it to refund your deposit.
        </p>
        <div class="vault-modal-grid">
          <div class="vault-modal-metric">
            <div class="vault-modal-metric-label">APY</div>
            <div class="vault-modal-metric-value tabular-nums">{formatPercent(offer.economics.yield_apy)}</div>
          </div>
          <div class="vault-modal-metric">
            <div class="vault-modal-metric-label">Term</div>
            <div class="vault-modal-metric-value tabular-nums">{offer.days_to_expiry}d</div>
          </div>
          <div class="vault-modal-metric">
            <div class="vault-modal-metric-label">Per-term return</div>
            <div class="vault-modal-metric-value tabular-nums">{formatPercent(newReturn)}</div>
            {returnChanged && (
              <div class="vault-modal-metric-was tabular-nums">was {formatPercent(origReturn)}</div>
            )}
          </div>
          <div class="vault-modal-metric">
            <div class="vault-modal-metric-label">Maturity</div>
            <div class="vault-modal-metric-value">{offer.maturity_date}</div>
            {maturityChanged && (
              <div class="vault-modal-metric-was">was {origMaturity}</div>
            )}
          </div>
        </div>
        <p class="vault-modal-risk">
          If BTC price at expiry exceeds <strong class="tabular-nums">{formatUsd(offer.short_leg.strike)}</strong>
          {shortChanged && <span class="vault-modal-was-inline tabular-nums"> (was {formatUsd(origShortStrike)})</span>},
          you forfeit a portion of your deposit.
          Losses capped at <strong class="tabular-nums">{formatPercent(offer.economics.max_loss_pct)}</strong>
          {maxLossChanged && <span class="vault-modal-was-inline tabular-nums"> (was {formatPercent(origMaxLoss)})</span>}
          {' '}(above{' '}
          <strong class="tabular-nums">{formatUsd(offer.long_leg.strike)}</strong>
          {longChanged && <span class="vault-modal-was-inline tabular-nums"> (was {formatUsd(origLongStrike)})</span>}).
        </p>
        {errorMessage && <p class="vault-modal-error">{errorMessage}</p>}
        <div class="vault-modal-actions">
          <button
            class="btn btn-secondary"
            onClick={onReject}
            disabled={loading}
          >
            {loading ? 'Working...' : 'Reject & refund'}
          </button>
          <button
            class="btn btn-primary"
            onClick={onAccept}
            disabled={loading}
          >
            {loading ? 'Working...' : 'Accept new offer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Refund-in-progress modal (pre-T32 B2) ─────────────────────────────

function RefundModal({ onAcknowledge }: { onAcknowledge: () => void }) {
  return (
    <div class="vault-modal-overlay">
      <div class="vault-modal" onClick={(e) => e.stopPropagation()}>
        <h2 class="vault-modal-title">Yield generation failed</h2>
        <p class="vault-modal-desc">
          We were unable to generate yield for this vault. Your deposit will be refunded
          to your refund address automatically — you don't need to do anything. You can
          try again with a new offer once the refund settles, or contact support if you
          need help.
        </p>
        <div class="vault-modal-actions">
          <button
            class="btn btn-primary"
            onClick={onAcknowledge}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Shared components ─────────────────────────────────────────────────

// COB-355: DepositAddressField + mempoolAddressUrlForNetwork moved to
// components/deposit-address-field.tsx so pages stop importing a UI
// primitive from this page module.

function VaultField({ label, value, mono, tabular, yours }: {
  label: string;
  value: string;
  mono?: boolean;
  tabular?: boolean;
  yours?: boolean;
}) {
  return (
    <div class={`vault-field${yours ? ' vault-field--yours' : ''}`}>
      <div class="vault-field-label">
        {label}
        {yours && <span class="vault-field-badge">Your wallet</span>}
      </div>
      <div class={`vault-field-value${mono ? ' mono' : ''}${tabular ? ' tabular-nums' : ''}`}>
        {value}
      </div>
    </div>
  );
}

// ── Withdrawal helpers ────────────────────────────────────────────────

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── Vault transaction history ─────────────────────────────────────────

const TX_TYPE_LABELS: Record<string, string> = {
  deposit: 'Deposit',
  reward: 'Reward',
  withdrawal: 'Withdrawal',
  refund: 'Refund',
};

function VaultTransactions({ txs }: { txs: VaultTx[] }) {
  const [expandedTxid, setExpandedTxid] = useState<string | null>(null);

  return (
    <section class="vault-section">
      <h2 class="vault-section-title">Transactions</h2>
      <div class="vault-tx-list">
        {txs.map((tx) => {
          const expanded = expandedTxid === tx.txid;
          const toggle = () => setExpandedTxid(expanded ? null : tx.txid);
          return (
            <div
              key={tx.txid}
              class={`vault-tx-row${expanded ? ' vault-tx-row--expanded' : ''}`}
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              onClick={toggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle();
                }
              }}
            >
              <div class="vault-tx-summary">
                <div class="vault-tx-left">
                  <span class={`vault-tx-type vault-tx-type--${tx.type}`}>
                    {TX_TYPE_LABELS[tx.type] ?? tx.type}
                  </span>
                  {tx.block_time ? (
                    <span class="vault-tx-time">
                      {new Date(tx.block_time * 1000).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  ) : (
                    <span class="vault-tx-time vault-tx-time--pending">Unconfirmed</span>
                  )}
                </div>
                <div class="vault-tx-right">
                  {tx.amount_sats != null && (
                    <span class={`vault-tx-amount tabular-nums${tx.direction === 'credit' ? ' vault-tx-amount--credit' : ' vault-tx-amount--debit'}`}>
                      {tx.direction === 'credit' ? '+' : '−'}{formatBtc(Math.abs(tx.amount_sats))} BTC
                    </span>
                  )}
                  {tx.counterparty && (
                    <span class="vault-tx-counterparty mono">{tx.counterparty}</span>
                  )}
                </div>
              </div>
              {expanded && (
                <div class="vault-tx-detail">
                  <div class="vault-tx-detail-row">
                    <span class="vault-tx-detail-label">Txid</span>
                    <span class="vault-tx-detail-value mono">{tx.txid}</span>
                  </div>
                  <div class="vault-tx-detail-row">
                    <span class="vault-tx-detail-label">Status</span>
                    <span class="vault-tx-detail-value">{tx.confirmed ? 'Confirmed' : 'Unconfirmed'}</span>
                  </div>
                  {tx.counterparty && (
                    <div class="vault-tx-detail-row">
                      <span class="vault-tx-detail-label">Counterparty</span>
                      <span class="vault-tx-detail-value mono">{tx.counterparty}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
